import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Inject shimmer keyframes once
const shimmerStyleId = 'ai-shimmer-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(shimmerStyleId)) {
    const style = document.createElement('style');
    style.id = shimmerStyleId;
    style.textContent = `
        @keyframes aiShimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
        }
        @keyframes aiPulse {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 0.9; }
        }
        @keyframes aiTextColor {
            0%, 100% { color: #34C6F1; }
            50% { color: #1FDD8F; }
        }
        @keyframes aiSpinnerColor {
            0%, 100% { color: #34C6F1; }
            50% { color: #1FDD8F; }
        }
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    `;
    document.head.appendChild(style);
}

const getCategoryIcon = (category) => {
    const cat = category?.toUpperCase() || '';
    if (cat.includes('ACTA') || cat.includes('PODER')) return 'pi-file-pdf';
    if (cat.includes('INE') || cat.includes('IDENTIFICACION')) return 'pi-id-card';
    if (cat.includes('SITUACION') || cat.includes('FISCAL') || cat.includes('RFC')) return 'pi-building';
    if (cat.includes('COMPROBANTE') && !cat.includes('INGRESO')) return 'pi-image';
    return 'pi-file';
};

const getScoreColor = (score) => {
    if (score >= 80) return '#23b56e';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
};

const statusStyles = {
    ok: {
        wrapper: { border: '1px solid #e2e8f0' },
        headerParams: { backgroundColor: '#e6f8f0', borderBottom: '1px solid #bbf7d0', color: '#1a8b54' },
        headerIcon: 'pi-check-circle',
        headerLabel: 'RECOMENDACIÓN: APROBAR',
        scoreBadge: { border: '1px solid #bbf7d0', color: '#1a8b54' },
    },
    warning: {
        wrapper: { border: '1px solid rgba(245, 158, 11, 0.3)', boxShadow: '0 0 0 1px rgba(245, 158, 11, 0.2)' },
        headerParams: { backgroundColor: '#fef3c7', borderBottom: '1px solid #fde68a', color: '#9a3412' },
        headerIcon: 'pi-exclamation-triangle',
        headerLabel: 'RECOMENDACIÓN: REVISAR',
        scoreBadge: { border: '1px solid #fde68a', color: '#9a3412' },
    },
    error: {
        wrapper: { border: '1px solid rgba(239, 68, 68, 0.3)', boxShadow: '0 0 0 1px rgba(239, 68, 68, 0.2)' },
        headerParams: { backgroundColor: '#fef2f2', borderBottom: '1px solid #fecaca', color: '#991b1b' },
        headerIcon: 'pi-times-circle',
        headerLabel: 'RECOMENDACIÓN: RECHAZAR',
        scoreBadge: { border: '1px solid #fecaca', color: '#991b1b' },
    }
};

const reviewBadgeStyles = {
    approved: { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#bbf7d0', label: 'Aprobado', icon: 'pi-check-circle' },
    pending: { backgroundColor: '#f1f5f9', color: '#64748b', borderColor: '#e2e8f0', label: 'Pendiente', icon: 'pi-clock' },
};

const AnalysisCard = ({ item, persona, isExpanded, isReanalyzing, onToggleExpand, onAprobar, onRechazar, onReanalizar, onVerDiscrepancia, onVerDocumento, onRetryAnalysis }) => {
    const [isApproving, setIsApproving] = React.useState(false);
    const [isRetrying, setIsRetrying] = React.useState(false);

    const extracted = item.original_data?.ai_analysis?.extracted_json || item.original_data?.ai_analysis?.extracted || {};
    const isInvalidType = extracted.es_documento_valido === false;

    const {
        category = 'Documento Desconocido',
        status: originalStatus = 'warning',
        accuracy = 0,
        finding = [],
        fileName,
        created_at,
        review_status = 'pending',
    } = item || {};

    const status = isInvalidType ? 'error' : originalStatus;

    const currentStyles = statusStyles[status] || statusStyles.warning;
    const iconName = getCategoryIcon(category);
    const scoreColor = getScoreColor(accuracy);

    const fileLabel = fileName ? fileName.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ') : null;

    const parsedFindings = Array.isArray(finding) ? finding.map(f => {
        if (typeof f === 'string') return { message: f, is_valid: status === 'ok' && !isInvalidType };
        return f;
    }) : [];

    const isApproved = review_status === 'approved';
    const reviewBadge = reviewBadgeStyles[review_status] || reviewBadgeStyles.pending;

    const renderCircle = (size = 50) => {
        const r = size * 0.36;
        const c = 2 * Math.PI * r;
        const filled = (accuracy / 100) * c;
        const empty = c - filled;
        const color = getScoreColor(accuracy);
        return (
            <div style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={3} />
                    <circle
                        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={3}
                        strokeLinecap="round"
                        strokeDasharray={`${filled} ${empty}`}
                        style={{ transition: 'stroke-dasharray 0.8s ease-in-out' }}
                    />
                </svg>
                <span style={{
                    position: 'absolute', fontSize: size * 0.22, fontWeight: 'bold', color: color
                }}>
                    {accuracy}%
                </span>
            </div>
        );
    };

    const renderActionButtons = () => {
        // No analysisItemId means the AI job failed before creating any DB record.
        // None of the backend actions (approve/reject/reanalyze) can work without it.
        if (!item.analysisItemId) {
            return (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.6rem 0.75rem', borderRadius: '0.5rem',
                    backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                    color: '#991b1b', fontSize: '0.75rem', fontWeight: '500',
                }}>
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.9rem', flexShrink: 0 }}></i>
                    <span style={{ flex: 1 }}>El análisis no se completó.</span>
                    <button
                        disabled={isRetrying}
                        onClick={async (e) => {
                            e.stopPropagation();
                            if (onRetryAnalysis) {
                                setIsRetrying(true);
                                try {
                                    await onRetryAnalysis(item);
                                } finally {
                                    setIsRetrying(false);
                                }
                            }
                        }}
                        style={{
                            padding: '0.35rem 0.75rem', borderRadius: '0.375rem', border: 'none',
                            backgroundColor: isRetrying ? '#7dd3fc' : '#0ea5e9', color: '#ffffff', fontWeight: '600', fontSize: '0.7rem',
                            cursor: isRetrying ? 'wait' : 'pointer', transition: 'background-color 0.2s', whiteSpace: 'nowrap',
                        }}
                        onMouseOver={(e) => !isRetrying && (e.currentTarget.style.backgroundColor = '#0284c7')}
                        onMouseOut={(e) => !isRetrying && (e.currentTarget.style.backgroundColor = '#0ea5e9')}
                    >
                        {isRetrying ? (
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.7rem', marginRight: '0.25rem' }}></i>
                        ) : (
                            <i className="pi pi-refresh" style={{ fontSize: '0.7rem', marginRight: '0.25rem' }}></i>
                        )}
                        {isRetrying ? 'Procesando...' : 'Reintentar análisis'}
                    </button>
                </div>
            );
        }

        if (isApproved) {
            return (
                <motion.div
                    initial={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                    animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
                >
                    <div style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
                        padding: '0.5rem', borderRadius: '0.5rem', backgroundColor: '#dcfce7',
                        color: '#166534', fontSize: '0.8rem', fontWeight: '600',
                    }}>
                        <motion.i
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
                            className="pi pi-check-circle"
                            style={{ fontSize: '1rem' }}
                        ></motion.i>
                        Documento Aprobado
                    </div>
                    {/* Mini reanalyze button */}
                    <button
                        onClick={(e) => { e.stopPropagation(); onReanalizar && onReanalizar(item); }}
                        title="Reanalizar"
                        style={{
                            width: '2rem', height: '2rem', borderRadius: '0.375rem', border: '1px solid #e2e8f0',
                            backgroundColor: '#f8fafc', color: '#94a3b8', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s', flexShrink: 0,
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#e0f2fe'; e.currentTarget.style.color = '#0284c7'; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.color = '#94a3b8'; }}
                    >
                        <i className="pi pi-refresh" style={{ fontSize: '0.75rem' }}></i>
                    </button>
                </motion.div>
            );
        }

        return (
            <motion.div
                key="pending-buttons"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch', flexWrap: 'wrap' }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', flex: '1 1 200px' }}>
                    <button
                        disabled={isApproving}
                        onClick={(e) => { e.stopPropagation(); onVerDiscrepancia && onVerDiscrepancia(item); }}
                        style={{
                            flex: '1 1 min-content', minWidth: '70px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
                            padding: '0.5rem 0.25rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0',
                            backgroundColor: '#ffffff', color: '#475569', fontWeight: '500', fontSize: '0.75rem',
                            cursor: isApproving ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s',
                            opacity: isApproving ? 0.5 : 1
                        }}
                        onMouseOver={(e) => !isApproving && (e.currentTarget.style.backgroundColor = '#f8fafc')}
                        onMouseOut={(e) => !isApproving && (e.currentTarget.style.backgroundColor = '#ffffff')}
                    >
                        <i className="pi pi-eye" style={{ fontSize: '0.85rem' }}></i>
                        Ver
                    </button>
                    <button
                        disabled={isApproving}
                        onClick={async (e) => {
                            e.stopPropagation();
                            if (onAprobar) {
                                setIsApproving(true);
                                try {
                                    await onAprobar(item);
                                } finally {
                                    setIsApproving(false);
                                }
                            }
                        }}
                        style={{
                            flex: '1 1 min-content', minWidth: '70px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
                            padding: '0.5rem 0.25rem', borderRadius: '0.5rem', border: 'none',
                            backgroundColor: isApproving ? '#7dd3fc' : '#0ea5e9', color: '#ffffff', fontWeight: '500', fontSize: '0.75rem',
                            cursor: isApproving ? 'wait' : 'pointer', transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => !isApproving && (e.currentTarget.style.backgroundColor = '#0284c7')}
                        onMouseOut={(e) => !isApproving && (e.currentTarget.style.backgroundColor = '#0ea5e9')}
                    >
                        {isApproving ? <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.85rem' }}></i> : <i className="pi pi-check" style={{ fontSize: '0.85rem' }}></i>}
                        {isApproving ? '...' : 'Aprobar'}
                    </button>
                    <button
                        disabled={isApproving}
                        onClick={(e) => { e.stopPropagation(); onRechazar && onRechazar(item); }}
                        style={{
                            flex: '1 1 min-content', minWidth: '70px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
                            padding: '0.5rem 0.25rem', borderRadius: '0.5rem', border: 'none',
                            backgroundColor: '#ef4444', color: '#ffffff', fontWeight: '500', fontSize: '0.75rem',
                            cursor: isApproving ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s',
                            opacity: isApproving ? 0.5 : 1
                        }}
                        onMouseOver={(e) => !isApproving && (e.currentTarget.style.backgroundColor = '#dc2626')}
                        onMouseOut={(e) => !isApproving && (e.currentTarget.style.backgroundColor = '#ef4444')}
                    >
                        <i className="pi pi-ban" style={{ fontSize: '0.85rem' }}></i>
                        Rechazar
                    </button>
                </div>
                {/* Mini reanalyze button */}
                <button
                    disabled={isApproving}
                    onClick={(e) => { e.stopPropagation(); onReanalizar && onReanalizar(item); }}
                    title="Reanalizar"
                    style={{
                        width: '2.1rem', height: 'auto', borderRadius: '0.375rem', border: '1px solid #e2e8f0',
                        backgroundColor: '#f8fafc', color: '#94a3b8', cursor: isApproving ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s', flexShrink: 0, opacity: isApproving ? 0.5 : 1
                    }}
                    onMouseOver={(e) => { if (!isApproving) { e.currentTarget.style.backgroundColor = '#e0f2fe'; e.currentTarget.style.color = '#0284c7'; } }}
                    onMouseOut={(e) => { if (!isApproving) { e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.color = '#94a3b8'; } }}
                >
                    <i className="pi pi-refresh" style={{ fontSize: '0.85rem' }}></i>
                </button>
            </motion.div>
        );
    };

    const isPM = persona && (persona.selectedTypePerson === 2 || persona.tipo_persona === 'PM' || persona.typePerson === 2 || persona.company);
    const isAvalCard = category?.toUpperCase().includes('AVAL') || (isPM && category?.toUpperCase().includes('IDENTIFICACIONES'));

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            whileHover={{ y: -3, boxShadow: '0 10px 20px -3px rgba(0, 0, 0, 0.12)' }}
            transition={{ duration: 0.2 }}
            style={{
                backgroundColor: '#ffffff', borderRadius: '1rem', overflow: 'hidden',
                display: 'flex', flexDirection: 'column', position: 'relative',
                ...(isApproved ? { border: '1px solid #bbf7d0' } : currentStyles.wrapper)
            }}
        >
            {/* Reanalyzing shimmer overlay */}
            {isReanalyzing && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10,
                    borderRadius: '1rem', overflow: 'hidden', pointerEvents: 'none',
                }}>
                    {/* Shimmer gradient band */}
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'linear-gradient(90deg, rgba(52,198,241,0.10) 0%, rgba(31,221,143,0.20) 25%, rgba(52,198,241,0.15) 50%, rgba(31,221,143,0.20) 75%, rgba(52,198,241,0.10) 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'aiShimmer 2.5s ease-in-out infinite',
                    }} />
                    {/* Frosted overlay with text */}
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        animation: 'aiPulse 2s ease-in-out infinite',
                    }}>
                        <i className="pi pi-spinner" style={{ fontSize: '1.5rem', marginBottom: '0.5rem', animation: 'aiSpinnerColor 3s ease-in-out infinite, spin 1s linear infinite' }}></i>
                        <span style={{ fontSize: '0.8rem', fontWeight: '600', animation: 'aiTextColor 3s ease-in-out infinite' }}>
                            Reanalizando con IA...
                        </span>
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                            Esto puede tomar unos segundos
                        </span>
                    </div>
                </div>
            )}

            {/* Header status */}
            <div
                onClick={() => onToggleExpand && onToggleExpand(item.id)}
                style={{
                    padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none',
                    ...currentStyles.headerParams
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <motion.i
                        animate={{ rotate: isExpanded ? -180 : 0 }}
                        transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 20 }}
                        className="pi pi-chevron-down"
                        style={{ fontSize: '0.75rem' }}
                    />
                    <i className={`pi ${currentStyles.headerIcon}`} style={{ fontSize: '1.25rem' }}></i>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', letterSpacing: '0.025em' }}>
                        {currentStyles.headerLabel}
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {/* Human review badge */}
                    <span style={{
                        fontSize: '9px', fontWeight: 'bold', padding: '0.125rem 0.5rem', borderRadius: '9999px',
                        border: `1px solid ${reviewBadge.borderColor}`, backgroundColor: reviewBadge.backgroundColor,
                        color: reviewBadge.color, display: 'flex', alignItems: 'center', gap: '0.25rem',
                    }}>
                        <i className={`pi ${reviewBadge.icon}`} style={{ fontSize: '9px' }}></i>
                        {reviewBadge.label}
                    </span>
                    <span style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.6)', fontSize: '10px', fontWeight: 'bold',
                        padding: '0.125rem 0.5rem', borderRadius: '9999px', whiteSpace: 'nowrap', ...currentStyles.scoreBadge
                    }}>
                        {accuracy}/100
                    </span>
                </div>
            </div>

            <div style={{ padding: '1.25rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Title row with circle */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{
                        width: '3rem', height: '4rem', backgroundColor: '#f1f5f9', borderRadius: '0.25rem',
                        border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <i className={`pi ${iconName}`} style={{ color: '#94a3b8', fontSize: '1.5rem' }}></i>
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.125rem' }}>
                            <h3 style={{ fontWeight: 'bold', color: '#0f172a', fontSize: '1rem', margin: 0 }}>{category}</h3>
                            {isAvalCard && (
                                <span style={{
                                    backgroundColor: '#7e22ce',
                                    color: '#ffffff',
                                    fontSize: '0.65rem',
                                    padding: '0.15rem 0.5rem',
                                    borderRadius: '9999px',
                                    fontWeight: 'bold',
                                    letterSpacing: '0.05em'
                                }}>AVAL</span>
                            )}
                        </div>
                        {fileLabel && (
                            <p style={{ fontSize: '0.7rem', color: '#94a3b8', margin: '0 0 0.25rem 0', fontWeight: '400' }}>{fileLabel}</p>
                        )}
                        <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 0.25rem 0' }}>
                            {created_at && created_at !== 'Sin fecha' ? `Subido: ${created_at}` : ''}
                        </p>
                        {isInvalidType && (
                            <span style={{
                                backgroundColor: '#ef4444',
                                color: '#ffffff',
                                fontSize: '0.65rem',
                                padding: '0.15rem 0.5rem',
                                borderRadius: '9999px',
                                fontWeight: 'bold',
                                letterSpacing: '0.05em',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                marginBottom: '0.5rem'
                            }}>
                                <i className="pi pi-times-circle" style={{ fontSize: '0.7rem' }}></i>
                                NO CORRESPONDE
                            </span>
                        )}
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onVerDiscrepancia) onVerDiscrepancia(item);
                            }}
                            style={{
                                fontSize: '0.75rem', color: '#0ea5e9', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem',
                                background: 'none', border: 'none', padding: 0, cursor: 'pointer'
                            }}
                        >
                            Ver documento <i className="pi pi-external-link" style={{ fontSize: '10px' }}></i>
                        </button>
                    </div>
                    {renderCircle(56)}
                </div>

                {/* Collapsed: action buttons */}
                {!isExpanded && (
                    <div style={{ marginTop: 'auto' }}>
                        {renderActionButtons()}
                    </div>
                )}

                {/* Expanded section */}
                <AnimatePresence initial={false}>
                    {isExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0, y: -10 }}
                            animate={{ height: 'auto', opacity: 1, y: 0 }}
                            exit={{ height: 0, opacity: 0, y: -10 }}
                            transition={{
                                height: { type: "tween", duration: 0.25, ease: "easeInOut" },
                                opacity: { duration: 0.2 },
                                y: { duration: 0.2 }
                            }}
                            style={{
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-evenly',
                                height: '-webkit-fill-available'
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                                {parsedFindings.map((f, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                                        <i className={`pi ${f.is_valid ? 'pi-check-circle' : 'pi-exclamation-triangle'}`}
                                            style={{ color: f.is_valid ? '#23b56e' : '#f59e0b', fontSize: '1.125rem', marginTop: '2px' }}
                                        ></i>
                                        <span style={{
                                            fontSize: '0.875rem',
                                            color: f.is_valid ? '#334155' : '#0f172a',
                                            fontWeight: f.is_valid ? 'normal' : '600',
                                            backgroundColor: f.is_valid ? 'transparent' : '#fff7ed',
                                            padding: f.is_valid ? '0' : '0 0.25rem',
                                            borderRadius: '0.25rem'
                                        }}>
                                            {f.message}
                                        </span>
                                    </div>
                                ))}
                                {parsedFindings.length === 0 && (
                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>No hay detalles extraídos.</div>
                                )}
                            </div>

                            <div style={{ marginTop: 'auto' }}>
                                {renderActionButtons()}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

export default AnalysisCard;
